import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getTheme } from "./theme/registry";
import { applyTokens } from "./theme/applyTokens";
import { resolveActiveThemeId } from "./theme/resolveActiveTheme";

const VisualEngineContext = createContext(null);

const STORAGE_KEY = "tl.userThemeId";

function readStoredThemeId() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return raw;
  } catch {
    return null;
  }
}

function writeStoredThemeId(id) {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // ignore
  }
}

export function VisualEngineProvider({ children }) {
  const [userThemeId, setUserThemeId] = useState(() => readStoredThemeId());

  const themeId = useMemo(() => {
    return resolveActiveThemeId({
      userThemeId,
      orgThemeId: null,
    });
  }, [userThemeId]);

  const theme = useMemo(() => getTheme(themeId), [themeId]);

  useEffect(() => {
    // Apply all CSS tokens from this theme to :root
    applyTokens(theme.tokens);
    document.documentElement.setAttribute("data-theme-id", themeId);
  }, [themeId, theme]);

  const value = useMemo(
    () => ({
      themeId,
      theme,
      setThemeId: (id) => {
        setUserThemeId(id);
        writeStoredThemeId(id);
      },
    }),
    [themeId, theme]
  );

  return <VisualEngineContext.Provider value={value}>{children}</VisualEngineContext.Provider>;
}

export function useVisualEngine() {
  const ctx = useContext(VisualEngineContext);
  if (!ctx) throw new Error("useVisualEngine must be used within VisualEngineProvider");
  return ctx;
}
